import { z } from "zod";
import {
  entityItemCategories,
  entityRelationshipLabels,
  type CharacterEntityData,
  type CreatureEntityData,
  type EntityCardData,
  type EntityCardEditorInput,
  type EntityCardType,
  type EntityItemCategory,
  type EntityRelationshipDraft,
  type ItemEntityData,
  type LocationEntityData,
  type MachineEntityData,
  type StoredEntityCardType,
} from "@/types/entity";

const entityUuidSchema = z.string().uuid();
const nullableUuidSchema = entityUuidSchema.nullable();
const optionalEditorTextSchema = z.string().default("");
const nullableStoredTextSchema = z.string().trim().min(1).nullable();

const imageFileSchema = z
  .custom<File | null | undefined>(
    (value) =>
      value === undefined
      || value === null
      || (typeof File !== "undefined" && value instanceof File),
    "Invalid file",
  )
  .optional();

const characterEditorSchema = z.object({
  ageText: optionalEditorTextSchema,
  aliases: z.array(z.string().trim().min(1)).default([]),
  imageFile: imageFileSchema,
  name: z.string().trim().min(1, "Name is required."),
  notes: optionalEditorTextSchema,
  personalityTraits: optionalEditorTextSchema,
  physicalDescription: optionalEditorTextSchema,
  raceLoreEntryId: nullableUuidSchema.default(null),
  removeImage: z.boolean().optional().default(false),
  skillsAndAbilities: optionalEditorTextSchema,
  type: z.literal("character"),
});

const itemEditorSchema = z.object({
  currentHolderEntityCardId: nullableUuidSchema.default(null),
  imageFile: imageFileSchema,
  itemCategory: z.enum(["weapon", "artifact", "tool", "relic", "technology"]),
  itemDescription: optionalEditorTextSchema,
  name: z.string().trim().min(1, "Name is required."),
  origin: optionalEditorTextSchema,
  removeImage: z.boolean().optional().default(false),
  type: z.literal("item-object"),
});

const creatureEditorSchema = z.object({
  creatureAbilities: optionalEditorTextSchema,
  creatureDescription: optionalEditorTextSchema,
  imageFile: imageFileSchema,
  name: z.string().trim().min(1, "Name is required."),
  removeImage: z.boolean().optional().default(false),
  speciesLoreEntryId: nullableUuidSchema.default(null),
  type: z.literal("creature"),
});

const locationEditorSchema = z.object({
  imageFile: imageFileSchema,
  locationDescription: optionalEditorTextSchema,
  locationNotes: optionalEditorTextSchema,
  name: z.string().trim().min(1, "Name is required."),
  removeImage: z.boolean().optional().default(false),
  type: z.literal("location"),
});

const machineEditorSchema = z.object({
  currentHolderEntityCardId: nullableUuidSchema.default(null),
  imageFile: imageFileSchema,
  machineCapabilities: optionalEditorTextSchema,
  machineDescription: optionalEditorTextSchema,
  name: z.string().trim().min(1, "Name is required."),
  origin: optionalEditorTextSchema,
  removeImage: z.boolean().optional().default(false),
  type: z.literal("machine"),
});

const characterStoredSchema = z.object({
  ageText: nullableStoredTextSchema.default(null),
  aliases: z.array(z.string().trim().min(1)).default([]),
  notes: nullableStoredTextSchema.default(null),
  personalityTraits: nullableStoredTextSchema.default(null),
  physicalDescription: nullableStoredTextSchema.default(null),
  raceLoreEntryId: nullableUuidSchema.default(null),
  skillsAndAbilities: nullableStoredTextSchema.default(null),
});

const itemStoredSchema = z.object({
  currentHolderEntityCardId: nullableUuidSchema.default(null),
  description: nullableStoredTextSchema.default(null),
  itemCategory: z.enum(entityItemCategories),
  origin: nullableStoredTextSchema.default(null),
});

const creatureStoredSchema = z.object({
  abilities: nullableStoredTextSchema.default(null),
  description: nullableStoredTextSchema.default(null),
  speciesLoreEntryId: nullableUuidSchema.default(null),
});

const locationStoredSchema = z.object({
  description: nullableStoredTextSchema.default(null),
  notes: nullableStoredTextSchema.default(null),
});

const machineStoredSchema = z.object({
  capabilities: nullableStoredTextSchema.default(null),
  currentHolderEntityCardId: nullableUuidSchema.default(null),
  description: nullableStoredTextSchema.default(null),
  origin: nullableStoredTextSchema.default(null),
});

export const createEntityCardSchema = z.discriminatedUnion("type", [
  characterEditorSchema,
  itemEditorSchema,
  creatureEditorSchema,
  locationEditorSchema,
  machineEditorSchema,
]);

export const updateEntityCardSchema = createEntityCardSchema;

export const entityRelationshipSchema = z
  .object({
    customLabel: z.string().default(""),
    label: z.enum(entityRelationshipLabels),
    targetId: entityUuidSchema,
    targetKind: z.enum(["entity-card", "lore-entry"]),
  })
  .superRefine((value, context) => {
    if (value.label === "custom" && value.customLabel.trim().length === 0) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Custom relationship labels cannot be blank.",
        path: ["customLabel"],
      });
    }
  });

function normalizeOptionalText(value: string | null | undefined) {
  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function normalizeAliases(value: string[] | undefined) {
  return (value ?? [])
    .map((alias) => alias.trim())
    .filter(Boolean)
    .filter((alias, index, aliases) => aliases.indexOf(alias) === index);
}

export function buildEntityRecordPayload(input: EntityCardEditorInput): EntityCardData {
  const validated = createEntityCardSchema.parse(input);

  switch (validated.type) {
    case "character":
      return {
        ageText: normalizeOptionalText(validated.ageText),
        aliases: normalizeAliases(validated.aliases),
        notes: normalizeOptionalText(validated.notes),
        personalityTraits: normalizeOptionalText(validated.personalityTraits),
        physicalDescription: normalizeOptionalText(validated.physicalDescription),
        raceLoreEntryId: validated.raceLoreEntryId ?? null,
        skillsAndAbilities: normalizeOptionalText(validated.skillsAndAbilities),
      } satisfies CharacterEntityData;
    case "item-object":
      return {
        currentHolderEntityCardId: validated.currentHolderEntityCardId ?? null,
        description: normalizeOptionalText(validated.itemDescription),
        itemCategory: validated.itemCategory as EntityItemCategory,
        origin: normalizeOptionalText(validated.origin),
      } satisfies ItemEntityData;
    case "creature":
      return {
        abilities: normalizeOptionalText(validated.creatureAbilities),
        description: normalizeOptionalText(validated.creatureDescription),
        speciesLoreEntryId: validated.speciesLoreEntryId ?? null,
      } satisfies CreatureEntityData;
    case "location":
      return {
        description: normalizeOptionalText(validated.locationDescription),
        notes: normalizeOptionalText(validated.locationNotes),
      } satisfies LocationEntityData;
    case "machine":
      return {
        capabilities: normalizeOptionalText(validated.machineCapabilities),
        currentHolderEntityCardId: validated.currentHolderEntityCardId ?? null,
        description: normalizeOptionalText(validated.machineDescription),
        origin: normalizeOptionalText(validated.origin),
      } satisfies MachineEntityData;
  }
}

export function parseEntityCardData(type: StoredEntityCardType, data: unknown): EntityCardData {
  const normalized = typeof data === "object" && data !== null ? data : {};

  switch (type) {
    case "character":
      return characterStoredSchema.parse(normalized) satisfies CharacterEntityData;
    case "item":
      return itemStoredSchema.parse(normalized) satisfies ItemEntityData;
    case "creature":
      return creatureStoredSchema.parse(normalized) satisfies CreatureEntityData;
    case "location":
      return locationStoredSchema.parse(normalized) satisfies LocationEntityData;
    case "machine":
      return machineStoredSchema.parse(normalized) satisfies MachineEntityData;
    default:
      throw new Error(`Unsupported entity type: ${String(type)}`);
  }
}

export function parseEntityRelationshipDraft(input: EntityRelationshipDraft) {
  return entityRelationshipSchema.parse(input);
}

export function buildEntityRelationshipLabel(input: EntityRelationshipDraft) {
  const validated = parseEntityRelationshipDraft(input);
  return validated.label === "custom" ? validated.customLabel.trim() : validated.label;
}

export function getStoredRelationshipType(
  targetKind: EntityRelationshipDraft["targetKind"],
) {
  return targetKind === "entity-card" ? "entity_entity" : "entity_lore";
}

export function isEntityCardType(value: string): value is EntityCardType {
  return value === "character"
    || value === "item-object"
    || value === "creature"
    || value === "location"
    || value === "machine";
}
